import React, { useState, useEffect } from 'react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Select,
    VStack,
    useToast,
    HStack,
    Flex,
} from '@chakra-ui/react';
import api from '../../../auth/apiClient';
import * as XLSX from 'xlsx-js-style';

import { WaterLevelReportPopupProps, FilterOptions, WaterLevelData } from './types';
import { trackDownload } from '../../../activity';

export const WaterLevelReportPopup: React.FC<WaterLevelReportPopupProps> = ({ isOpen, onClose }) => {
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [selectedEstate, setSelectedEstate] = useState<string>('');
    const [selectedDivision, setSelectedDivision] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState<FilterOptions>({
        companies: [],
        estates: [],
        divisis: []
    });
    const toast = useToast();

    // Generate years from 2020 to 2030
    const years = Array.from({ length: 11 }, (_, i) => 2020 + i);

    // Generate months
    const months = [
        { value: 1, label: 'Januari' },
        { value: 2, label: 'Februari' },
        { value: 3, label: 'Maret' },
        { value: 4, label: 'April' },
        { value: 5, label: 'Mei' },
        { value: 6, label: 'Juni' },
        { value: 7, label: 'Juli' },
        { value: 8, label: 'Agustus' },
        { value: 9, label: 'September' },
        { value: 10, label: 'Oktober' },
        { value: 11, label: 'November' },
        { value: 12, label: 'Desember' },
    ];

    // Fetch options from API
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const estatesResponse = await api.get('/inspeksi/web/estate-db');
                const optionsResponse = await api.get('/inspeksi/options');

                setOptions({
                    companies: optionsResponse.data.companies || [],
                    estates: estatesResponse.data || [],
                    divisis: optionsResponse.data.divisis || []
                });
            } catch (error) {
                console.error("Error fetching options", error);
            }
        };

        if (isOpen) {
            fetchOptions();
        }
    }, [isOpen]);

    // Reset estate and division when company changes
    useEffect(() => {
        setSelectedEstate('');
        setSelectedDivision('');
    }, [selectedCompany]);

    // Reset division when estate changes
    useEffect(() => {
        setSelectedDivision('');
    }, [selectedEstate]);

    // Get filtered estates based on selected company
    const filteredEstates = options.estates.filter(estate =>
        !selectedCompany || estate.company_id === parseInt(selectedCompany)
    );

    // Get filtered divisions based on selected estate
    const filteredDivisions = options.divisis.filter(division =>
        !selectedEstate || division.estate_id === parseInt(selectedEstate)
    );

    // Get company name for header
    const getCompanyCode = () => {
        if (!selectedCompany) return 'GBS';
        const company = options.companies.find(c => c.id === parseInt(selectedCompany));
        return company ? (company.id === 1 ? 'GBS' : 'LKK') : 'GBS';
    };

    const handleExport = async () => {
        if (!selectedYear || !selectedMonth || !selectedCompany) {
            toast({
                title: 'Error',
                description: 'Silakan pilih tahun, bulan, dan company',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            // Build API URL
            const baseUrl = '/water-level/report';

            const monthStr = selectedMonth.toString().padStart(2, '0');
            const periodStr = `${selectedYear}${monthStr}`;

            const params: any = {
                period: periodStr,
                company_id: parseInt(selectedCompany)
            };

            if (selectedEstate) params.estate_id = parseInt(selectedEstate);
            if (selectedDivision) params.divisi_id = parseInt(selectedDivision);

            console.log('Request URL & params:', baseUrl, params);

            const response = await api.get(baseUrl, { params });
            const data: WaterLevelData[] = response.data;

            if (!data || data.length === 0) {
                toast({
                    title: 'Info',
                    description: 'Tidak ada data untuk periode dan filter yang dipilih',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }

            // Create Excel workbook
            const workbook = XLSX.utils.book_new();

            // Prepare data for Excel
            const excelData = prepareExcelData(data);

            // Create worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(excelData);

            // Apply styling
            applyExcelStyling(worksheet, excelData.length);

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Water Level');

            // Generate filename
            const monthName = months.find(m => m.value === selectedMonth)?.label || selectedMonth.toString();
            const companyCode = getCompanyCode();
            const filename = `water_level_${companyCode}_${monthName}_${selectedYear}.xlsx`;

            // Save file
            XLSX.writeFile(workbook, filename);
            trackDownload('Laporan Water Level', { filename });

            toast({
                title: 'Berhasil',
                description: 'Laporan Water Level berhasil diunduh',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            onClose();
        } catch (error) {
            console.error('Error exporting water level report:', error);
            toast({
                title: 'Error',
                description: 'Gagal mengunduh laporan water level',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const prepareExcelData = (data: WaterLevelData[]) => {
        const monthName = months.find(m => m.value === selectedMonth)?.label || '';
        const companyCode = getCompanyCode();

        // water_level_GBS_Februari_2026
        const titleText = `Water Level ${companyCode} ${monthName} ${selectedYear}`;

        // Row 1: Title
        const excelData: any[][] = [
            [titleText, '', '', '', '', '', '', '', '']
        ];

        // Row 2: Headers
        excelData.push([
            'Tanggal', 'Category', 'Estate', 'Divisi', 'Blok', 'Water Level', 'WMS Status', 'Petugas', 'Jabatan'
        ]);

        // Row 3+: Data
        data.forEach(item => {
            excelData.push([
                item.tanggal || '',
                item.category || '',
                item.estate || '',
                item.divisi || '',
                item.blok || '',
                item.water_level !== null && item.water_level !== undefined ? item.water_level : '',
                item.wms_status || '',
                item.petugas || '',
                item.jabatan || ''
            ]);
        });

        return excelData;
    };

    const getBasicBorderStyle = () => ({
        border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        }
    });

    const applyExcelStyling = (worksheet: XLSX.WorkSheet, totalRows: number) => {
        // Set column widths
        worksheet['!cols'] = [
            { wch: 15 }, // tanggal
            { wch: 20 }, // category
            { wch: 25 }, // estate
            { wch: 20 }, // divisi
            { wch: 15 }, // blok
            { wch: 12 }, // water_level
            { wch: 12 }, // wms_status
            { wch: 25 }, // petugas
            { wch: 20 }, // jabatan
        ];

        // Merge cells for headers
        worksheet['!merges'] = [
            {
                s: { r: 0, c: 0 },
                e: { r: 0, c: 8 }
            }
        ];

        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:I3');

        for (let row = range.s.r; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                const cell = worksheet[cellAddress];

                if (!cell) continue;
                if (!cell.s) cell.s = {};

                if (row === 0) {
                    // Title
                    cell.s = {
                        font: { bold: true, sz: 12 },
                        alignment: { horizontal: 'center', vertical: 'center' }
                    };
                } else if (row === 1) {
                    // Header
                    cell.s = {
                        ...getBasicBorderStyle(),
                        font: { bold: true, sz: 10 },
                        alignment: { horizontal: 'left', vertical: 'center' },
                        fill: { fgColor: { rgb: 'D9D9D9' } } // Gray background
                    };
                } else {
                    // Data
                    cell.s = {
                        ...getBasicBorderStyle(),
                        font: { sz: 10 },
                        alignment: {
                            horizontal: col === 5 ? 'right' : 'left', // water_level is right aligned
                            vertical: 'center'
                        }
                    };
                }
            }
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Unduh Laporan Water Level</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <VStack spacing={4}>
                        <HStack width="100%" spacing={4}>
                            <FormControl>
                                <FormLabel>Tahun</FormLabel>
                                <Select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                >
                                    {years.map(year => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Bulan</FormLabel>
                                <Select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                >
                                    {months.map(month => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </HStack>

                        <FormControl isRequired>
                            <FormLabel>Company</FormLabel>
                            <Select
                                placeholder="Pilih Company"
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                            >
                                {options.companies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Estate (Opsional)</FormLabel>
                            <Select
                                placeholder="Pilih Estate"
                                value={selectedEstate}
                                onChange={(e) => setSelectedEstate(e.target.value)}
                                disabled={!selectedCompany}
                            >
                                {filteredEstates.map(estate => (
                                    <option key={estate.id} value={estate.id}>
                                        {estate.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Divisi (Opsional)</FormLabel>
                            <Select
                                placeholder="Pilih Divisi"
                                value={selectedDivision}
                                onChange={(e) => setSelectedDivision(e.target.value)}
                                disabled={!selectedEstate}
                            >
                                {filteredDivisions.map(division => (
                                    <option key={division.id} value={division.id}>
                                        {division.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <Flex width="100%" justifyContent="flex-end" gap={3}>
                            <Button variant="ghost" onClick={onClose}>
                                Batal
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleExport}
                                isLoading={isLoading}
                                loadingText="Mengunduh..."
                            >
                                Unduh Excel
                            </Button>
                        </Flex>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default WaterLevelReportPopup;
